<div class="space-y-6">
  <app-page-header
    [backTo]="['/wallets']"
    backLabel="Retour aux wallets"
  />

  @if (loadError() || loading()) {
    <app-data-state
      [loading]="loading()"
      [error]="loadError()"
      loadingMessage="Chargement du wallet…"
      (retry)="fetch()"
    />
  } @else if (snapshot(); as s) {
    <!-- Header card -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 class="text-xl font-semibold text-gray-800 dark:text-white/90">
            Wallet — {{ typeLabel[ownerType] }} #{{ ownerRef }}
          </h1>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Devise <strong>{{ s.credit.currency }}</strong>
            @if (actorLink(); as link) {
              <span class="mx-2 text-gray-300 dark:text-gray-600">·</span>
              <a [routerLink]="link" class="text-brand-600 hover:text-brand-700 dark:text-brand-400">
                Voir la fiche
              </a>
            }
          </p>
        </div>
        @if (s.debt > 0) {
          <span class="inline-flex h-fit items-center gap-1 rounded-full bg-error-50 px-3 py-1 text-xs font-medium text-error-700 dark:bg-error-500/10 dark:text-error-400">
            ⚠ En dette ({{ s.debt | number:'1.0-0' }} {{ s.credit.currency }})
          </span>
        }
      </div>

      <!-- 4 KPI cards -->
      <div class="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div class="rounded-xl border px-4 py-3"
             [class.border-brand-200]="s.credit.balance >= 0"
             [class.border-error-200]="s.credit.balance < 0"
             [class.dark:border-brand-800]="s.credit.balance >= 0"
             [class.dark:border-error-800]="s.credit.balance < 0">
          <p class="text-xs text-gray-500 dark:text-gray-400">CREDIT (réserve)</p>
          <p class="mt-1 text-lg font-semibold"
             [class.text-brand-700]="s.credit.balance >= 0"
             [class.text-error-700]="s.credit.balance < 0"
             [class.dark:text-brand-400]="s.credit.balance >= 0"
             [class.dark:text-error-400]="s.credit.balance < 0">
            {{ s.credit.balance | number:'1.0-0' }} <span class="text-xs font-normal text-gray-400">{{ s.credit.currency }}</span>
          </p>
        </div>

        <div class="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
          <p class="text-xs text-gray-500 dark:text-gray-400">BALANCE (gains)</p>
          <p class="mt-1 text-lg font-semibold text-gray-800 dark:text-white/90">
            {{ s.balance.balance | number:'1.0-0' }} <span class="text-xs font-normal text-gray-400">{{ s.balance.currency }}</span>
          </p>
        </div>

        <div class="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
          <p class="text-xs text-gray-500 dark:text-gray-400">Retirable</p>
          <p class="mt-1 text-lg font-semibold text-gray-800 dark:text-white/90">
            {{ s.withdrawableBalance | number:'1.0-0' }} <span class="text-xs font-normal text-gray-400">{{ s.credit.currency }}</span>
          </p>
          @if (s.debt > 0) {
            <p class="mt-0.5 text-xs text-gray-400">BALANCE gelée à hauteur de la dette</p>
          }
        </div>

        <div class="rounded-xl border px-4 py-3"
             [class.border-error-200]="s.debt > 0"
             [class.border-gray-200]="s.debt === 0"
             [class.dark:border-error-800]="s.debt > 0"
             [class.dark:border-gray-800]="s.debt === 0">
          <p class="text-xs text-gray-500 dark:text-gray-400">Dette</p>
          <p class="mt-1 text-lg font-semibold"
             [class.text-error-700]="s.debt > 0"
             [class.text-gray-800]="s.debt === 0"
             [class.dark:text-error-400]="s.debt > 0"
             [class.dark:text-white/90]="s.debt === 0">
            {{ s.debt | number:'1.0-0' }} <span class="text-xs font-normal text-gray-400">{{ s.credit.currency }}</span>
          </p>
        </div>
      </div>
    </section>

    <!-- Account details -->
    <div class="grid gap-6 lg:grid-cols-2">
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ accountTypeLabel[s.credit.accountType] }}</h2>
        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">ID compte</dt>
            <dd class="text-right font-mono text-gray-800 dark:text-white/90">#{{ s.credit.id }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Statut</dt>
            <dd class="text-right">
              <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                [class.bg-brand-50]="s.credit.accountStatus === 'ACTIVE'"
                [class.text-brand-700]="s.credit.accountStatus === 'ACTIVE'"
                [class.bg-warning-50]="s.credit.accountStatus === 'FROZEN'"
                [class.text-warning-700]="s.credit.accountStatus === 'FROZEN'"
                [class.bg-gray-100]="s.credit.accountStatus === 'CLOSED'"
                [class.text-gray-700]="s.credit.accountStatus === 'CLOSED'"
              >
                {{ accountStatusLabel[s.credit.accountStatus] }}
              </span>
            </dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Solde</dt>
            <dd class="text-right"
              [class.text-brand-700]="s.credit.balance >= 0"
              [class.text-error-700]="s.credit.balance < 0"
              [class.dark:text-brand-400]="s.credit.balance >= 0"
              [class.dark:text-error-400]="s.credit.balance < 0">
              {{ s.credit.balance | number:'1.0-0' }} {{ s.credit.currency }}
            </dd>
          </div>
        </dl>
      </section>

      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ accountTypeLabel[s.balance.accountType] }}</h2>
        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">ID compte</dt>
            <dd class="text-right font-mono text-gray-800 dark:text-white/90">#{{ s.balance.id }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Statut</dt>
            <dd class="text-right">
              <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                [class.bg-brand-50]="s.balance.accountStatus === 'ACTIVE'"
                [class.text-brand-700]="s.balance.accountStatus === 'ACTIVE'"
                [class.bg-warning-50]="s.balance.accountStatus === 'FROZEN'"
                [class.text-warning-700]="s.balance.accountStatus === 'FROZEN'"
                [class.bg-gray-100]="s.balance.accountStatus === 'CLOSED'"
                [class.text-gray-700]="s.balance.accountStatus === 'CLOSED'"
              >
                {{ accountStatusLabel[s.balance.accountStatus] }}
              </span>
            </dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Solde</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ s.balance.balance | number:'1.0-0' }} {{ s.balance.currency }}</dd>
          </div>
        </dl>
      </section>
    </div>

    <!-- Ledger history -->
    <section class="rounded-2xl border border-gray-200 bg-white shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Historique</h2>
          <p class="text-xs text-gray-500 dark:text-gray-400">Mouvements postés sur le compte sélectionné.</p>
        </div>
        <!-- Toggle tabs -->
        <div class="flex h-10 rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-800 dark:bg-white/[0.02]">
          <button
            type="button"
            (click)="switchLedger('CREDIT')"
            class="rounded-md px-4 text-xs font-medium transition"
            [class.bg-white]="ledgerType() === 'CREDIT'"
            [class.text-gray-800]="ledgerType() === 'CREDIT'"
            [class.shadow-theme-xs]="ledgerType() === 'CREDIT'"
            [class.dark:bg-gray-900]="ledgerType() === 'CREDIT'"
            [class.dark:text-white/90]="ledgerType() === 'CREDIT'"
            [class.text-gray-500]="ledgerType() !== 'CREDIT'"
            [class.dark:text-gray-400]="ledgerType() !== 'CREDIT'"
          >
            CREDIT
          </button>
          <button
            type="button"
            (click)="switchLedger('BALANCE')"
            class="rounded-md px-4 text-xs font-medium transition"
            [class.bg-white]="ledgerType() === 'BALANCE'"
            [class.text-gray-800]="ledgerType() === 'BALANCE'"
            [class.shadow-theme-xs]="ledgerType() === 'BALANCE'"
            [class.dark:bg-gray-900]="ledgerType() === 'BALANCE'"
            [class.dark:text-white/90]="ledgerType() === 'BALANCE'"
            [class.text-gray-500]="ledgerType() !== 'BALANCE'"
            [class.dark:text-gray-400]="ledgerType() !== 'BALANCE'"
          >
            BALANCE
          </button>
        </div>
      </div>

      @if (ledgerError() || (ledgerLoading() && ledger().length === 0) || ledger().length === 0) {
        <app-data-state
          [loading]="ledgerLoading()"
          [error]="ledgerError()"
          [empty]="!ledgerLoading() && !ledgerError() && ledger().length === 0"
          loadingMessage="Chargement de l'historique…"
          emptyMessage="Aucun mouvement enregistré sur ce compte."
          (retry)="loadLedger()"
        />
      } @else {
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
            <thead class="bg-gray-50 dark:bg-white/[0.02]">
              <tr>
                <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Date</th>
                <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Sens</th>
                <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Mémo</th>
                <th class="px-5 py-3 text-right text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Montant</th>
                <th class="px-5 py-3 text-right text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Solde après</th>
                <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Transfer</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
              @for (e of ledger(); track e.id) {
                <tr class="transition hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                  <td class="whitespace-nowrap px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                    {{ e.postedAt | ldate:'dd/MM/yy HH:mm:ss' }}
                  </td>
                  <td class="whitespace-nowrap px-5 py-4">
                    <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                      [class.bg-brand-50]="e.direction === 'CREDIT'"
                      [class.text-brand-700]="e.direction === 'CREDIT'"
                      [class.bg-error-50]="e.direction === 'DEBIT'"
                      [class.text-error-700]="e.direction === 'DEBIT'"
                    >
                      {{ directionLabel[e.direction] }}
                    </span>
                  </td>
                  <td class="px-5 py-4 text-sm text-gray-700 dark:text-gray-300">{{ e.memo || '—' }}</td>
                  <td class="whitespace-nowrap px-5 py-4 text-right text-sm font-medium"
                    [class.text-brand-700]="e.direction === 'CREDIT'"
                    [class.text-error-700]="e.direction === 'DEBIT'"
                    [class.dark:text-brand-400]="e.direction === 'CREDIT'"
                    [class.dark:text-error-400]="e.direction === 'DEBIT'">
                    {{ e.direction === 'CREDIT' ? '+' : '−' }} {{ e.amount | number:'1.0-0' }} <span class="text-xs font-normal text-gray-400">{{ e.currency }}</span>
                  </td>
                  <td class="whitespace-nowrap px-5 py-4 text-right text-sm text-gray-700 dark:text-gray-300">
                    {{ e.resultingBalance | number:'1.0-0' }}
                  </td>
                  <td class="whitespace-nowrap px-5 py-4 font-mono text-xs text-gray-500 dark:text-gray-400">
                    @if (e.transferId) { #{{ e.transferId }} } @else { — }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (ledgerTotalPages() > 0) {
          <div class="flex flex-col gap-3 border-t border-gray-200 px-5 py-4 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Page {{ ledgerPage() }} sur {{ ledgerTotalPages() }} — {{ ledgerTotalItems() }} mouvement(s)
            </p>
            <div class="flex flex-wrap items-center gap-1">
              <button type="button" (click)="goToLedgerPage(ledgerPage() - 1)" [disabled]="ledgerPage() <= 1" class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/5">Précédent</button>
              @for (n of ledgerPageWindow(); track n) {
                <button
                  type="button"
                  (click)="goToLedgerPage(n)"
                  class="min-w-[36px] rounded-lg border px-3 py-1.5 text-xs font-medium transition"
                  [class.border-brand-500]="n === ledgerPage()"
                  [class.bg-brand-500]="n === ledgerPage()"
                  [class.text-white]="n === ledgerPage()"
                  [class.border-gray-300]="n !== ledgerPage()"
                  [class.text-gray-700]="n !== ledgerPage()"
                  [class.hover:bg-gray-50]="n !== ledgerPage()"
                  [class.dark:border-gray-700]="n !== ledgerPage()"
                  [class.dark:text-gray-400]="n !== ledgerPage()"
                  [class.dark:hover:bg-white/5]="n !== ledgerPage()"
                >
                  {{ n }}
                </button>
              }
              <button type="button" (click)="goToLedgerPage(ledgerPage() + 1)" [disabled]="ledgerPage() >= ledgerTotalPages()" class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/5">Suivant</button>
            </div>
          </div>
        }
      }
    </section>
  }
</div>
